// [claude-code 2026-05-20] SOL-62: Todo + Queue drawer sliding up from composer
import type { IssueTrackingType, TodoItem } from "./hooks/useTodoList";
import { MessageQueue, type QueuedMessage } from "./MessageQueue";
import { RepoChatComposerSurface } from "./composer/RepoChatComposer";
import { TodoProgressGlyph } from "./TodoProgressGlyph";
import {
  CheckCircle2,
  ChevronDown,
  Circle,
  Loader2,
  X,
} from "../icon-bank/AnimatedIcons";
import { FintheonNumericText } from "../numbers/FintheonNumericText";
import { VoiceAuroraOrb } from "../voice/VoiceAuroraOrb";

interface TodoDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  todos: TodoItem[];
  onToggleTodo: (id: string) => void;
  onRemoveTodo: (id: string) => void;
  queue: QueuedMessage[];
  onEditQueue: (id: string, text: string) => void;
  onRemoveQueue: (id: string) => void;
  onReorderQueue?: (fromIdx: number, toIdx: number) => void;
  onSendQueueOne?: () => void;
  onSteerQueue?: (id: string) => void;
  onQueueQueuedMessage?: (id: string) => void;
  onPullQueueToDraft?: (id: string) => void;
  approvalPending?: boolean;
  agentActive?: boolean;
  showTodos?: boolean;
}

type TodoStage = "todo" | "in_progress" | "awaiting_review" | "complete";

const ISSUE_TYPE_META: Record<IssueTrackingType, { label: string }> = {
  task: { label: "Task" },
  bug: { label: "Bug" },
  feature: { label: "Feature" },
  risk: { label: "Risk" },
  chore: { label: "Chore" },
  issue: { label: "Issue" },
};

function issueTypeLabel(type?: IssueTrackingType): string {
  return ISSUE_TYPE_META[type ?? "task"].label;
}

const TODO_STATUS_COLOR = "var(--fintheon-secondary, var(--fintheon-muted))";

function getTodoStage(
  todo: TodoItem,
  approvalPending?: boolean,
  agentActive?: boolean,
): TodoStage {
  if (todo.done) return "complete";
  if (approvalPending && todo.source === "harper-ui-tool") {
    return "awaiting_review";
  }
  if (agentActive && todo.source === "harper-ui-tool") {
    return "in_progress";
  }
  return "todo";
}

function StageIcon({ stage }: { stage: TodoStage }) {
  if (stage === "complete") {
    return (
      <CheckCircle2
        size={16}
        strokeWidth={2.2}
        color={TODO_STATUS_COLOR}
        aria-hidden="true"
      />
    );
  }
  if (stage === "in_progress") {
    return (
      <Loader2
        size={13}
        strokeWidth={2.2}
        color={TODO_STATUS_COLOR}
        className="animate-spin"
        aria-hidden="true"
      />
    );
  }
  return (
    <Circle
      size={11}
      strokeWidth={2.2}
      color={TODO_STATUS_COLOR}
      aria-hidden="true"
    />
  );
}

function stageLabel(stage: TodoStage): string {
  if (stage === "complete") return "Complete";
  if (stage === "in_progress") return "In progress";
  if (stage === "awaiting_review") return "Awaiting review";
  return "To-do";
}

export function TodoDrawer({
  isOpen,
  onClose,
  todos,
  onToggleTodo,
  onRemoveTodo,
  queue,
  onEditQueue,
  onRemoveQueue,
  onReorderQueue,
  onSendQueueOne,
  onSteerQueue,
  onQueueQueuedMessage,
  onPullQueueToDraft,
  approvalPending,
  agentActive,
  showTodos = true,
}: TodoDrawerProps) {
  const hasTodos = todos.length > 0;
  const hasQueue = queue.length > 0;
  const showTodoList = showTodos && hasTodos;

  const pendingTodos = todos.filter((t) => !t.done);
  const doneCount = todos.length - pendingTodos.length;
  const isComplete = hasTodos && doneCount === todos.length;
  const isWorking = !!agentActive && pendingTodos.length > 0;

  return (
    <RepoChatComposerSurface
      open={isOpen}
      maxHeight="340px"
      className={`fintheon-todo-placement-drawer fintheon-todo-morph-card solvys-liquid-glass ${
        hasQueue ? "fintheon-message-queue-drawer" : ""
      } ${
        hasQueue && !showTodoList ? "fintheon-message-queue-only-drawer" : ""
      }`}
      style={{
        borderColor:
          "color-mix(in srgb, var(--fintheon-accent) 18%, transparent)",
      }}
    >
      <div className="overflow-hidden">
        {showTodoList ? (
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              {isWorking ? (
                <VoiceAuroraOrb activity="shaping" size={18} />
              ) : (
                <TodoProgressGlyph
                  isComplete={isComplete}
                  active={isWorking}
                  doneCount={doneCount}
                  totalCount={todos.length}
                />
              )}
              <span className="truncate text-[13px] font-semibold leading-none text-[var(--fintheon-text)]/88">
                <FintheonNumericText text={doneCount} kind="count" />/
                <FintheonNumericText text={todos.length} kind="count" /> tasks
                completed
              </span>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 text-[var(--fintheon-text)]/45 transition-colors hover:text-[var(--fintheon-text)]/80"
              title="Close"
            >
              <ChevronDown size={16} />
            </button>
          </div>
        ) : null}

        <div
          className="fintheon-todo-drawer__body overflow-y-auto pb-3"
          style={{ maxHeight: showTodoList ? "286px" : "220px" }}
        >
          {showTodoList && (
            <div className="border-b border-[var(--fintheon-accent)]/8 py-1.5">
              <div className="pb-0.5">
                {todos.map((todo, index) => {
                  const stage = getTodoStage(
                    todo,
                    approvalPending,
                    agentActive,
                  );
                  const typeLabel = issueTypeLabel(todo.issueTrackingType);
                  return (
                    <div
                      key={todo.id}
                      className="fintheon-todo-stage-row group grid grid-cols-[18px_1.45rem_minmax(0,1fr)_16px] items-start gap-2 px-3 py-1.5 transition-colors hover:bg-[var(--fintheon-accent)]/5"
                      data-issue-type={todo.issueTrackingType ?? "task"}
                      data-stage={stage}
                    >
                      <button
                        key={`${todo.id}-${stage}`}
                        onClick={() => onToggleTodo(todo.id)}
                        className="fintheon-todo-stage-icon mt-0.5 shrink-0 transition-[color,transform,opacity] duration-200 hover:scale-110"
                        title={`${typeLabel} ${stageLabel(stage)}`}
                        aria-label={`${typeLabel} ${stageLabel(stage)}: ${todo.text}`}
                      >
                        <StageIcon stage={stage} />
                      </button>
                      <span className="text-right text-[13px] leading-5 text-[var(--fintheon-text)]/42">
                        <FintheonNumericText text={index + 1} />.
                      </span>
                      <span
                        className={`min-w-0 text-[13px] leading-5 ${
                          stage === "complete"
                            ? "text-[var(--fintheon-text)]/48"
                            : "text-[var(--fintheon-text)]/82"
                        }`}
                      >
                        <span
                          className={stage === "complete" ? "line-through" : ""}
                        >
                          {todo.text}
                        </span>
                      </span>
                      <button
                        onClick={() => onRemoveTodo(todo.id)}
                        className="mt-0.5 opacity-0 text-[var(--fintheon-text)]/30 transition-all hover:text-[var(--fintheon-accent)]/60 group-hover:opacity-100"
                        title="Remove task"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Queue section ─────────────────────────────────────────── */}
          {hasQueue && (
            <div>
              <div className="fintheon-todo-drawer__queue px-3 pb-3 pt-2">
                <MessageQueue
                  queue={queue}
                  onEdit={onEditQueue}
                  onRemove={onRemoveQueue}
                  onReorder={onReorderQueue}
                  onSendOne={onSendQueueOne}
                  onSteerMessage={onSteerQueue}
                  onQueueMessage={onQueueQueuedMessage}
                  onEditDraft={onPullQueueToDraft ?? undefined}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </RepoChatComposerSurface>
  );
}
